/* ==========================================================================
   page-content.js
   Builds the contents rail on the default page template.

   The list is generated from the h2 elements in the page body rather than
   configured per page, so it cannot fall out of sync with the content and
   needs no setup when a merchant adds a new policy page.

   Headings get an id only if they don't already have one, so hand written
   anchors in the page HTML keep working and any existing links to them stay
   valid.

   The rail hides itself below three headings, where a contents list is noise
   rather than navigation.
   ========================================================================== */

(function () {
  'use strict';

  var MIN_HEADINGS = 3;

  function slugify(text) {
    return String(text)
      .toLowerCase()
      .trim()
      .replace(/['’"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  /* ---------------------------------------------------------------------
     Policy pages

     Shopify renders privacy, refund, terms of service and shipping with its
     own .shopify-policy__* markup and ignores templates/policy.liquid on this
     store, so those pages came out looking nothing like the default page
     template: no masthead, no contents rail, and body text at the theme's
     .rte size instead of ours.

     Rather than trying to restyle Shopify's markup, which meant fighting
     `.rte:not(...) p` on specificity and still ending up with a different
     layout, this rebuilds the page into the exact same structure the default
     page template outputs. Identical markup means identical CSS, so the two
     page types cannot drift apart.

     Unwrapping the .rte div is the part that fixes the type sizes: once the
     content is no longer inside .rte, the theme's rule stops matching.
     --------------------------------------------------------------------- */

  function adoptPolicyMarkup() {
    var container = document.querySelector('.shopify-policy__container');
    if (!container) return;

    var body = container.querySelector('.shopify-policy__body');
    if (!body) return;

    var titleEl = container.querySelector('.shopify-policy__title h1, .shopify-policy__title h2');
    var title = titleEl ? titleEl.textContent.trim() : '';

    var section = document.createElement('section');
    section.className = 'pg';
    section.setAttribute('data-page-kind', 'policy');
    section.innerHTML =
      '<div class="pg-hero"><div class="pg__wrap pg-hero__inner">' +
      '<h1 class="pg-hero__title"></h1></div></div>' +
      '<div class="pg__wrap"><div class="pg-layout">' +
      '<aside class="pg-toc" hidden data-pg-toc>' +
      '<p class="pg-toc__title">On this page</p>' +
      '<ul class="pg-toc__list" data-pg-toc-list></ul></aside>' +
      '<div class="pg-prose" data-pg-prose></div>' +
      '</div></div>';

    section.querySelector('.pg-hero__title').textContent = title;

    // Move the nodes rather than copying innerHTML so anything already bound
    // to them (app scripts, embedded forms) survives the move.
    var prose = section.querySelector('[data-pg-prose]');
    var source = body.querySelector('.rte') || body;
    while (source.firstChild) prose.appendChild(source.firstChild);

    container.parentNode.replaceChild(section, container);
  }

  function init() {
    adoptPolicyMarkup();

    var prose = document.querySelector('[data-pg-prose]');
    var toc = document.querySelector('[data-pg-toc]');
    var list = document.querySelector('[data-pg-toc-list]');
    if (!prose || !toc || !list) return;

    var headings = Array.prototype.slice.call(prose.querySelectorAll('h2'));
    if (headings.length < MIN_HEADINGS) return;

    var used = {};
    var links = [];

    headings.forEach(function (h) {
      if (!h.id) {
        var base = slugify(h.textContent) || 'section';
        var id = base;
        var n = 2;
        // Duplicate headings are common in policy pages ("Returns" twice, say),
        // and a repeated id would send every link to the first one.
        while (used[id] || document.getElementById(id)) {
          id = base + '-' + n++;
        }
        h.id = id;
      }
      used[h.id] = true;

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      list.appendChild(li);
      links.push(a);
    });

    toc.hidden = false;

    // Opts the section into the two column layout. See the note in
    // page-content.css: the grid column must not exist until the rail does.
    var root = toc.closest('.pg');
    if (root) root.classList.add('pg--toc');

    /* --- current section highlight ---
       Tracks the last heading scrolled past rather than whichever is on screen,
       so the rail still shows where you are in the middle of a long clause. */
    if (!('IntersectionObserver' in window)) return;

    var visible = {};

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting;
        });

        var currentId = null;
        for (var i = 0; i < headings.length; i++) {
          var h = headings[i];
          if (visible[h.id]) {
            currentId = h.id;
            break;
          }
          if (h.getBoundingClientRect().top < 140) {
            currentId = h.id;
          }
        }

        links.forEach(function (a) {
          a.classList.toggle(
            'is-current',
            currentId !== null && a.getAttribute('href') === '#' + currentId
          );
        });
      },
      { rootMargin: '-120px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(function (h) {
      observer.observe(h);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-pg-prose]')) init();
  });
})();
